/**
 * Serviço para gerenciar notificações push no frontend
 * Integração com Firebase Cloud Messaging e backend
 */

import { getMessaging, getToken, onMessage } from "firebase/messaging";
import app from "../config/firebase";
import { API_ENDPOINTS } from "../config/api";

class NotificationService {
  constructor() {
    this.messaging = null;
    this.swRegistration = null;
    this.vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY; // VAPID key do Firebase
    this.initializeMessaging();
  }

  async initializeMessaging() {
    try {
      // Verificar se Firebase Messaging é suportado
      if (!("serviceWorker" in navigator)) {
        console.warn("[NotificationService] Service Worker não suportado");
        return;
      }

      if (!("Notification" in window)) {
        console.warn("[NotificationService] Notificações não suportadas");
        return;
      }

      // Verificar se VAPID key está configurada
      if (!this.vapidKey) {
        console.warn("[NotificationService] VAPID key não configurada");
        return;
      }

      // Aguardar o SW principal (sw.js) que já inclui Firebase Messaging
      try {
        this.swRegistration = await navigator.serviceWorker.ready;
        console.log(
          "[NotificationService] Service Worker ready:",
          this.swRegistration
        );
      } catch (swError) {
        console.error(
          "[NotificationService] Erro ao aguardar Service Worker:",
          swError
        );
      }

      // Inicializar messaging
      try {
        this.messaging = getMessaging(app);
        console.log("[NotificationService] Firebase Messaging inicializado");

        // Escutar mensagens em foreground
        this.setupForegroundListener();
      } catch (messagingError) {
        console.error(
          "[NotificationService] Erro ao inicializar Firebase Messaging:",
          messagingError
        );
        this.messaging = null;
      }
    } catch (error) {
      console.error(
        "[NotificationService] Erro geral ao inicializar messaging:",
        error
      );
      this.messaging = null;
    }
  }

  /**
   * Solicitar permissão e obter token FCM
   * @param {string} userId - ID do usuário
   * @returns {Promise<string|null>} Token FCM ou null
   */
  async requestPermissionAndGetToken(userId) {
    try {
      // Verificar se Firebase Messaging foi inicializado
      if (!this.messaging) {
        console.warn(
          "[NotificationService] Firebase Messaging não inicializado - tentando reinicializar"
        );
        await this.initializeMessaging();

        if (!this.messaging) {
          throw new Error("Firebase Messaging não pôde ser inicializado");
        }
      }

      // Verificar se notificações são suportadas
      if (!("Notification" in window)) {
        throw new Error("Notificações não são suportadas neste navegador");
      }

      // Verificar se VAPID key está disponível
      if (!this.vapidKey) {
        throw new Error("VAPID key não configurada");
      }

      // Verificar se já tem permissão
      const permission = await Notification.requestPermission();

      if (permission !== "granted") {
        console.warn("[NotificationService] Permissão de notificação negada");
        return null;
      }

      // Garantir que temos o service worker pronto
      if (!this.swRegistration) {
        this.swRegistration = await navigator.serviceWorker.ready;
      }

      // Obter token FCM vinculado ao service worker correto
      const token = await getToken(this.messaging, {
        vapidKey: this.vapidKey,
        serviceWorkerRegistration: this.swRegistration,
      });

      if (token) {
        console.log(
          "[NotificationService] Token FCM obtido:",
          token.substring(0, 20) + "..."
        );

        // Registrar token no backend
        const registered = await this.registerToken(userId, token);

        if (registered) {
          return token;
        } else {
          console.warn(
            "[NotificationService] Falha ao registrar token no backend"
          );
          return token; // Retornar token mesmo se registro falhar
        }
      } else {
        console.warn(
          "[NotificationService] Nenhum token de registro disponível"
        );
        return null;
      }
    } catch (error) {
      console.error("[NotificationService] Erro ao obter token:", error);

      // Log adicional para debugging
      console.error("[NotificationService] Detalhes do erro:", {
        messaging: !!this.messaging,
        vapidKey: !!this.vapidKey,
        notificationSupport: "Notification" in window,
        serviceWorkerSupport: "serviceWorker" in navigator,
        permission:
          "Notification" in window ? Notification.permission : "not-supported",
      });

      return null;
    }
  }

  /**
   * Registrar token diretamente no Firestore (sem backend)
   * @param {string} userId - ID do usuário
   * @param {string} token - Token FCM
   * @returns {Promise<boolean>} Sucesso do registro
   */
  async registerToken(userId, token) {
    try {
      const { db } = await import("../config/firebase");
      const { doc, setDoc, collection, query, where, getDocs, writeBatch } = await import("firebase/firestore");

      const deviceInfo = this.getDeviceInfo();
      const now = new Date().toISOString();

      // Desativar tokens antigos deste usuário nesta mesma plataforma/browser
      try {
        const tokensRef = collection(db, "fcm_tokens");
        const oldTokensQuery = query(
          tokensRef,
          where("user_id", "==", userId),
          where("active", "==", true)
        );
        const oldTokensSnap = await getDocs(oldTokensQuery);

        if (oldTokensSnap.size > 0) {
          const batch = writeBatch(db);
          oldTokensSnap.forEach((docSnap) => {
            // Não desativar o token atual (caso seja re-registro)
            if (docSnap.id !== token) {
              batch.update(docSnap.ref, { active: false, deactivated_at: now, deactivated_reason: "new_token_registered" });
            }
          });
          await batch.commit();
          console.log(`[NotificationService] ${oldTokensSnap.size - 1} tokens antigos desativados para ${userId}`);
        }
      } catch (cleanupError) {
        console.warn("[NotificationService] Erro ao limpar tokens antigos:", cleanupError);
      }

      // Salvar novo token no Firestore
      const tokenDoc = doc(db, "fcm_tokens", token);
      await setDoc(tokenDoc, {
        token: token,
        user_id: userId,
        device_info: deviceInfo,
        created_at: now,
        updated_at: now,
        active: true,
      });

      console.log("[NotificationService] Token registrado no Firestore:", {
        userId,
        tokenPreview: token.substring(0, 20) + "...",
        device: deviceInfo.platform,
      });

      localStorage.setItem("fcm_token", token);
      localStorage.setItem("fcm_registered", "true");

      return true;
    } catch (error) {
      console.error(
        "[NotificationService] Erro ao registrar token no Firestore:",
        error
      );
      return false;
    }
  }

  /**
   * Configurar listener para mensagens em foreground
   */
  setupForegroundListener() {
    if (!this.messaging) return;

    onMessage(this.messaging, (payload) => {
      console.log(
        "[NotificationService] Mensagem recebida em foreground:",
        payload
      );

      const { notification, data } = payload;

      // Mostrar notificação custom ou usar a padrão do navegador
      this.showForegroundNotification(notification, data);
    });
  }

  /**
   * Mostrar notificação quando app está em foreground
   * @param {object} notification - Dados da notificação
   * @param {object} data - Dados customizados
   */
  async showForegroundNotification(notification, data) {
    if ("Notification" in window && Notification.permission === "granted") {
      try {
        const options = {
          body: notification.body,
          icon: notification.image || "/icons/icon-192x192.png",
          badge: "/icons/icon-72x72.png",
          tag: data?.type || "foreground-notification",
          data: data,
          requireInteraction: false,
        };

        // Usar showNotification via service worker (funciona no PWA standalone)
        const registration = this.swRegistration || await navigator.serviceWorker.ready;
        await registration.showNotification(notification.title, options);
      } catch (error) {
        console.error(
          "[NotificationService] Erro ao criar notificação:",
          error
        );
      }
    } else {
      const isSupported = "Notification" in window;
      console.warn("[NotificationService] Notificação não pode ser exibida:", {
        suportado: isSupported,
        permissao: isSupported ? Notification.permission : "not-supported",
      });
    }
  }

  /**
   * Enviar notificação de teste localmente (sem backend)
   * @param {string} userId - ID do usuário
   * @returns {Promise<boolean>} Sucesso do envio
   */
  async sendTestNotification(userId) {
    try {
      if ("Notification" in window && Notification.permission === "granted") {
        const registration = this.swRegistration || await navigator.serviceWorker.ready;
        await registration.showNotification("Teste PWA", {
          body: "Esta é uma notificação de teste do sistema da igreja",
          icon: "/icons/icon-192x192.png",
          badge: "/icons/icon-72x72.png",
          tag: "test-notification",
          requireInteraction: false,
        });

        console.log(
          "[NotificationService] Notificação de teste enviada via Service Worker"
        );
        return true;
      } else {
        console.warn(
          "[NotificationService] Permissão de notificação não concedida"
        );
        return false;
      }
    } catch (error) {
      console.error(
        "[NotificationService] Erro ao enviar notificação de teste:",
        error
      );
      return false;
    }
  }

  /**
   * Subscrever usuário a um tópico (salva no Firestore)
   * @param {string} userId - ID do usuário
   * @param {string} topic - Nome do tópico
   * @returns {Promise<boolean>} Sucesso da subscrição
   */
  async subscribeToTopic(userId, topic) {
    try {
      const { db } = await import("../config/firebase");
      const { doc, setDoc } = await import("firebase/firestore");

      const now = new Date().toISOString();

      // Salvar subscrição no Firestore
      const subscriptionDoc = doc(
        db,
        "topic_subscriptions",
        `${userId}_${topic}`
      );
      await setDoc(subscriptionDoc, {
        user_id: userId,
        topic: topic,
        subscribed_at: now,
        active: true,
      });

      console.log(
        `[NotificationService] Subscrito ao tópico no Firestore: ${topic}`
      );
      return true;
    } catch (error) {
      console.error("[NotificationService] Erro ao subscrever:", error);
      return false;
    }
  }

  /**
   * Obter informações do dispositivo
   * @returns {object} Informações do dispositivo
   */
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    let browser = "unknown";
    let platform = "web";

    // Detectar navegador
    if (userAgent.includes("Chrome")) browser = "chrome";
    else if (userAgent.includes("Firefox")) browser = "firefox";
    else if (userAgent.includes("Safari")) browser = "safari";
    else if (userAgent.includes("Edge")) browser = "edge";

    // Detectar plataforma
    if (userAgent.includes("Android")) platform = "android";
    else if (userAgent.includes("iPhone") || userAgent.includes("iPad"))
      platform = "ios";
    else if (userAgent.includes("Windows")) platform = "windows";
    else if (userAgent.includes("Mac")) platform = "macos";
    else if (userAgent.includes("Linux")) platform = "linux";

    return {
      platform,
      browser,
      userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };
  }

  /**
   * Verificar se notificações estão disponíveis
   * @returns {boolean} Disponibilidade das notificações
   */
  isNotificationSupported() {
    return "Notification" in window && "serviceWorker" in navigator;
  }

  /**
   * Obter status da permissão
   * @returns {string} Status da permissão
   */
  getPermissionStatus() {
    if (!this.isNotificationSupported()) {
      return "not-supported";
    }
    return Notification.permission;
  }

  /**
   * Verificar se token está registrado
   * @returns {boolean} Token registrado
   */
  isTokenRegistered() {
    return localStorage.getItem("fcm_registered") === "true";
  }

  /**
   * Limpar dados de registro
   */
  clearRegistration() {
    localStorage.removeItem("fcm_token");
    localStorage.removeItem("fcm_registered");
  }

  /**
   * Envia notificação para um tópico específico
   */
  async sendTopicNotification(topic, title, body, data = {}) {
    try {
      const response = await fetch(API_ENDPOINTS.sendPushNotification, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          topic,
          title,
          body,
          data,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log(
          "[NotificationService] Notificação para tópico enviada:",
          result
        );
        return result;
      } else {
        const error = await response.json();
        console.error(
          "[NotificationService] Erro ao enviar notificação para tópico:",
          error
        );
        return null;
      }
    } catch (error) {
      console.error(
        "[NotificationService] Erro ao enviar notificação para tópico:",
        error
      );
      return null;
    }
  }
}

// Instância singleton
const notificationService = new NotificationService();

export default notificationService;

// Funções de conveniência
export const requestNotificationPermission = (userId) => {
  return notificationService.requestPermissionAndGetToken(userId);
};

export const sendTestNotification = (userId) => {
  return notificationService.sendTestNotification(userId);
};

export const subscribeToAllMembers = (userId) => {
  return notificationService.subscribeToTopic(userId, "all_members");
};

export const isNotificationSupported = () => {
  return notificationService.isNotificationSupported();
};

export const getNotificationPermission = () => {
  return notificationService.getPermissionStatus();
};

/**
 * Envia uma notificação para um usuário específico
 * @param {string} userId - ID do usuário destinatário
 * @param {string} title - Título da notificação
 * @param {string} body - Corpo da mensagem
 * @param {string} type - Tipo de notificação
 * @param {string} referenceId - ID de referência (opcional)
 * @returns {Promise<boolean>}
 */
export const enviarNotificacao = async (
  userId,
  title,
  body,
  type = "info",
  referenceId = null
) => {
  try {
    if ("Notification" in window && Notification.permission === "granted") {
      const registration = notificationService.swRegistration || await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        body: body,
        icon: "/icons/icon-192x192.png",
        badge: "/icons/icon-72x72.png",
        tag: `${type}-${referenceId || Date.now()}`,
        data: {
          type,
          referenceId,
          userId,
          url: type === "irregularidade" && referenceId ? "/irregularidades" : "/",
        },
        requireInteraction: type === "urgente" || type === "irregularidade",
      });

      console.log("[NotificationService] Notificação enviada:", {
        userId,
        title,
        type,
      });

      return true;
    } else {
      console.warn(
        "[NotificationService] Permissão de notificação não concedida"
      );
      return false;
    }
  } catch (error) {
    console.error("[NotificationService] Erro ao enviar notificação:", error);
    return false;
  }
};
